<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterLink, useRoute, useRouter, type RouteRecordName } from 'vue-router';
import { useTranslation } from 'i18next-vue';

import type { MenuItem } from 'primevue/menuitem';
import type Menu from 'primevue/menu';

import { MoonIcon, SearchIcon, SunIcon, SupportIcon, UiButton } from '@peanuds/ui';
import {
  features,
  useAuthSessionStore,
  useColorScheme,
  useFeatureFlag,
  useHubspotChat,
} from '@peanuds/app-shell';
import { NotificationsNavItem } from '@peanuds/modules';
import {
  BATCH_COLLECTION_DRAFT_REVIEW_ROUTE_NAME,
  BATCH_TRANSACTION_DETAILS_ROUTE_NAME,
  BATCH_TRANSACTIONS_ROUTE_NAME,
} from '@peanuds/batch-collections';

import AppAccountSwitcher from './AppAccountSwitcher.vue';
import AppLogo from './AppLogo.vue';

interface Props {
  onLogout?: () => void;
  paymentsApprovalCount?: number;
  paymentsApprovalHasError?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  onLogout: () => {},
  paymentsApprovalCount: 0,
  paymentsApprovalHasError: false,
});

const router = useRouter();
const route = useRoute();
const { t } = useTranslation();
const isPaymentsApprovalEnabled = useFeatureFlag(features.paymentsApproval);
const isBatchCollectionsEnabled = useFeatureFlag(features.batchCollections);
const isDarkModeEnabled = useFeatureFlag(features.darkMode);
const { isDark, toggleColorScheme } = useColorScheme();

const NAVBAR_COLLAPSE_BREAKPOINT = '960px';
const NAVBAR_COLLAPSE_MEDIA_QUERY = `(max-width: ${NAVBAR_COLLAPSE_BREAKPOINT})`;

const paymentsApprovalDigitCount = computed(() => props.paymentsApprovalCount.toString().length);

type MenuInstance = InstanceType<typeof Menu>;
type MainNavigationItem = {
  icon: string;
  key: string;
  label: string;
  routeName: RouteRecordName;
  burgerLabel?: string;
};

const { isChatAvailable, openChat } = useHubspotChat();

const authSessionStore = useAuthSessionStore();
const userInitial = computed(() => {
  const name = [authSessionStore.givenName, authSessionStore.familyName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return name.charAt(0).toUpperCase();
});

const navigateToRoute = (routeName: RouteRecordName) => {
  router.push({ name: routeName });
};

const paymentsApprovalBurgerMenuLabel = computed(() => {
  if (props.paymentsApprovalHasError) {
    return `${t('nav.payments_approval')} (!)`;
  }

  if (props.paymentsApprovalCount > 0) {
    return `${t('nav.payments_approval')} (${props.paymentsApprovalCount})`;
  }

  return t('nav.payments_approval');
});

const mainNavigationItems = computed<MainNavigationItem[]>(() => [
  {
    label: t('nav.overview'),
    key: 'dashboard',
    routeName: 'dashboard',
    icon: 'pi pi-home',
  },
  {
    label: t('nav.send_money'),
    key: 'send-money',
    routeName: 'send-money',
    icon: 'pi pi-arrow-up-right',
  },
  ...(isBatchCollectionsEnabled.value
    ? [
        {
          label: t('nav.batch_collections'),
          key: 'batch-collections',
          routeName: BATCH_TRANSACTIONS_ROUTE_NAME,
          icon: 'pi pi-list-check',
        },
      ]
    : []),
  {
    label: t('nav.accounts'),
    key: 'accounts',
    routeName: 'accounts',
    icon: 'pi pi-wallet',
  },
  {
    label: t('nav.cards'),
    key: 'cards',
    routeName: 'cards',
    icon: 'pi pi-credit-card',
  },
  {
    label: t('nav.transactions'),
    key: 'transactions',
    routeName: 'transactions',
    icon: 'pi pi-list',
  },
  ...(isPaymentsApprovalEnabled.value
    ? [
        {
          label: t('nav.payments_approval'),
          burgerLabel: paymentsApprovalBurgerMenuLabel.value,
          key: 'payments-approval',
          routeName: 'payments-approval',
          icon: 'pi pi-check-circle',
        },
      ]
    : []),
]);

const isNavbarCollapsed = ref(false);
let navbarMediaQueryList: MediaQueryList | null = null;

const syncNavbarCollapsed = () => {
  if (!navbarMediaQueryList) {
    return;
  }

  isNavbarCollapsed.value = navbarMediaQueryList.matches;
};

onMounted(() => {
  navbarMediaQueryList = window.matchMedia(NAVBAR_COLLAPSE_MEDIA_QUERY);
  syncNavbarCollapsed();
  navbarMediaQueryList.addEventListener('change', syncNavbarCollapsed);
});

onUnmounted(() => {
  navbarMediaQueryList?.removeEventListener('change', syncNavbarCollapsed);
  navbarMediaQueryList = null;
});

const navbarMenuItems = computed<MenuItem[]>(() =>
  mainNavigationItems.value.map(({ key, label, routeName }) => ({
    label,
    key,
    command: () => {
      navigateToRoute(routeName);
    },
  })),
);

const burgerMenuItems = computed<MenuItem[]>(() => [
  ...(isNavbarCollapsed.value
    ? [
        {
          label: t('nav.main'),
          items: mainNavigationItems.value.map(({ burgerLabel, icon, key, label, routeName }) => ({
            label: burgerLabel ?? label,
            key,
            icon,
            command: () => {
              navigateToRoute(routeName);
            },
          })),
        },
        { separator: true },
      ]
    : []),
]);

const burgerMenu = ref<MenuInstance | null>();

const routeNameToMenuKey: Record<string, string> = {
  dashboard: 'dashboard',
  'send-money': 'send-money',
  'send-money-flow': 'send-money',
  'new-recipient': 'send-money',
  'existing-contact': 'send-money',
  myself: 'send-money',
  'payment-processed': 'send-money',
  [BATCH_TRANSACTIONS_ROUTE_NAME]: 'batch-collections',
  [BATCH_TRANSACTION_DETAILS_ROUTE_NAME]: 'batch-collections',
  [BATCH_COLLECTION_DRAFT_REVIEW_ROUTE_NAME]: 'batch-collections',
  accounts: 'accounts',
  'account-review': 'accounts',
  'open-account': 'accounts',
  cards: 'cards',
  'card-details': 'cards',
  'order-card': 'cards',
  'order-physical-card': 'cards',
  'card-result': 'cards',
  analytics: 'analytics',
  transactions: 'transactions',
  'payments-approval': 'payments-approval',
  settings: 'settings',
  'settings-company-details': 'settings',
  'settings-login-activity': 'settings',
  'settings-account-safety': 'settings',
  'settings-user-control': 'settings',
};

const resolveMenuKey = (routeName?: RouteRecordName | null) => {
  if (typeof routeName !== 'string') {
    return null;
  }

  return routeNameToMenuKey[routeName] ?? null;
};

const activeItemKey = computed(() => resolveMenuKey(route.name));

const toggleBurgerMenu = (event: MouseEvent) => {
  burgerMenu.value?.toggle(event);
};

const hideBurgerMenu = () => {
  burgerMenu.value?.hide();
};
</script>

<template>
  <Menubar :model="navbarMenuItems" :breakpoint="NAVBAR_COLLAPSE_BREAKPOINT">
    <template #start>
      <RouterLink to="/" class="navbar-logo">
        <AppLogo />
      </RouterLink>
    </template>

    <template #item="{ item, props: itemProps }">
      <div
        v-bind="itemProps.action"
        class="p-menuitem-link relative"
        :data-testid="`app-nav-main-${item.key}`"
        :class="{ 'app-nav__menu-item--active': activeItemKey === item.key }"
      >
        <span class="p-menuitem-text">{{ item.label }}</span>
        <Badge
          v-if="
            item.key === 'payments-approval' &&
            (paymentsApprovalHasError || paymentsApprovalCount > 0)
          "
          :value="paymentsApprovalHasError ? '!' : paymentsApprovalCount"
          :severity="paymentsApprovalHasError ? 'warn' : 'danger'"
          class="navbar-badge"
          :class="{
            'navbar-badge--two-digits':
              !paymentsApprovalHasError && paymentsApprovalDigitCount === 2,
            'navbar-badge--three-digits':
              !paymentsApprovalHasError && paymentsApprovalDigitCount >= 3,
          }"
        />
      </div>
    </template>

    <template #end>
      <div class="navbar-end">
        <UiButton
          severity="secondary"
          size="s"
          disabled
          :aria-label="t('common.search')"
          data-testid="app-nav-search"
        >
          <template #icon>
            <SearchIcon />
          </template>
        </UiButton>

        <UiButton
          v-if="isChatAvailable"
          severity="secondary"
          size="s"
          :aria-label="t('nav.support')"
          @click="openChat"
        >
          <template #icon>
            <SupportIcon />
          </template>
        </UiButton>

        <UiButton
          v-if="isDarkModeEnabled"
          severity="secondary"
          size="s"
          :aria-label="isDark ? t('nav.theme.to_light') : t('nav.theme.to_dark')"
          data-testid="app-nav-theme-toggle"
          @click="toggleColorScheme"
        >
          <template #icon>
            <SunIcon v-if="isDark" />
            <MoonIcon v-else />
          </template>
        </UiButton>

        <NotificationsNavItem />

        <button
          type="button"
          class="navbar-avatar"
          data-testid="app-nav-menu-toggle"
          aria-haspopup="true"
          aria-controls="burger_menu"
          :aria-label="t('nav.menu')"
          @click="toggleBurgerMenu"
        >
          {{ userInitial }}
        </button>

        <Menu ref="burgerMenu" id="burger_menu" :model="burgerMenuItems" :popup="true">
          <template #start>
            <AppAccountSwitcher @close="hideBurgerMenu" @logout="props.onLogout()" />
          </template>
        </Menu>
      </div>
    </template>
  </Menubar>
</template>

<style lang="scss">
.navbar-logo {
  padding: 0;
}

.navbar-end {
  display: flex;
  gap: $spacing-s;
  align-items: center;
}

.navbar-avatar {
  display: inline-flex;
  flex: 0 0 2rem;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-left: $spacing-sm;
  border: 0;
  border-radius: $radius-999;
  color: $color-text-inverse;
  background: $color-base-surface-200;
  font-size: $font-size-sm;
  font-weight: $font-weight-semibold;
  line-height: 1;
  cursor: pointer;
}

#burger_menu {
  --p-menu-list-padding: 0;
  min-width: 218px;
  padding: calc($spacing-xs + $spacing-2xs);
  border-color: var(--p-content-border-color);
  border-radius: var(--radius-12);
}

#burger_menu .p-menu-item-icon {
  color: var(--p-surface-400);
}

#burger_menu .p-menu-start {
  padding: 0;
}

#burger_menu .p-menu-start:empty {
  display: none;
}

#burger_menu .app-nav__apply-account-item .p-menu-item-content {
  margin: $static-space-2 0;
}

#burger_menu .p-menu-item-link {
  box-sizing: border-box;
  height: calc($static-space-8 + $static-space-1);
  min-height: calc($static-space-8 + $static-space-1);
}

#burger_menu .p-menu-separator {
  margin-inline: calc(-1 * #{$static-space-1});
}

#burger_menu .p-menu-item:last-child {
  margin-top: 1.25px;
}

.p-menubar-mobile .p-menubar-button {
  display: none !important;
}
</style>

<style lang="scss" scoped>
.p-menubar {
  max-width: 1600px;
  margin: 0 auto;
  padding: $spacing-s $spacing-xl;
  gap: $spacing-xl;
}

.navbar-badge {
  position: absolute;
  top: -9px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: $static-space-4;
  min-width: $static-space-4;
  padding: 0 $static-space-1;
  padding-top: 1px;
  font-size: $text-xs;
  line-height: 1;
  font-weight: $font-weight-semibold;
  border-radius: $radius-999;
  background: var(--p-badge-warn-background);
  outline: $static-space-1 * 0.5 solid var(--p-surface-0);

  &--two-digits {
    font-size: $static-text-xs * 0.6667;
  }

  &--three-digits {
    font-size: $static-text-xs * 0.5;
  }
}

.p-menuitem-link.relative {
  position: relative;
  overflow: visible;
}

.app-nav__menu-item--active {
  color: $color-text-primary;
  background: $color-menu-item-hover-bg;
  border-radius: var(--p-border-radius-xl);
}
</style>
